/**
 * The analytics allowlists both apps share (#379), and nothing else.
 *
 * Deliberately free of imports, like mobile's `lib/analyticsSchema.ts` which
 * re-exports these: they bound the values that can reach Google Analytics,
 * so anything that checks a value against them must be able to without
 * pulling in an SDK. Mobile imports this file by path, not through the core
 * barrel, so the jest mocks of `@smartmixin/core` leave it intact.
 */

/**
 * What an out-of-schema value degrades to. Never the raw value: a value we
 * failed to anticipate is exactly the case where "just send it" would leak
 * something.
 */
export const OTHER = 'other';

/**
 * The station connect funnel (#380 MR4): `provider` of `connect_start` and
 * `connect_success`. Snake_case like every other value sent, rather than
 * core's camelCase ids, which are the apps' business.
 */
export const CONNECT_PROVIDER_VALUES = [
  'netatmo_weather',
  'netatmo_energy',
  'ambient_weather',
  'davis',
  'ecowitt',
  'my_acurite',
  'weatherflow',
  'wunderground',
] as const;

export type ConnectProviderValue = (typeof CONNECT_PROVIDER_VALUES)[number];

/**
 * Core's `ProviderId` to the value sent. Keyed by plain strings here to keep
 * this file import-free; `analytics.ts` types it as a `Record` over core's
 * `ProviderId`, so tsc fails when core gains a provider it lacks, and the
 * parity test fails on one core dropped.
 */
export const CONNECT_PROVIDER_VALUE_BY_ID = {
  netatmoWeather: 'netatmo_weather',
  netatmoEnergy: 'netatmo_energy',
  ambientWeather: 'ambient_weather',
  davis: 'davis',
  ecowitt: 'ecowitt',
  myAcurite: 'my_acurite',
  weatherflow: 'weatherflow',
  wunderground: 'wunderground',
} as const satisfies Record<string, ConnectProviderValue>;

/**
 * `source` of the connect events: where the user set off to connect a
 * service. `onboarding` is the first-launch provider rows (#380 MR3).
 */
export const CONNECT_SOURCES = [
  'drawer',
  'settings',
  'home',
  'onboarding',
] as const;

export type ConnectSource = (typeof CONNECT_SOURCES)[number];

/**
 * The two parameters of `connect_start` and `connect_success`: the
 * provider's value and where the user set off from, each sent as OTHER when
 * not allowlisted (a missing source included). Own keys only: `constructor`
 * is not a provider.
 */
export const connectParams = (
  provider: string,
  source: string | undefined,
): {
  provider: ConnectProviderValue | typeof OTHER;
  source: ConnectSource | typeof OTHER;
} => {
  const byId: Record<string, ConnectProviderValue> =
    CONNECT_PROVIDER_VALUE_BY_ID;
  const sources: readonly string[] = CONNECT_SOURCES;
  return {
    provider: Object.prototype.hasOwnProperty.call(byId, provider)
      ? byId[provider]
      : OTHER,
    source:
      source != null && sources.includes(source)
        ? (source as ConnectSource)
        : OTHER,
  };
};

/** `kind` of `favorite_add` / `favorite_remove` (#379). */
export const FAVORITE_KINDS = ['station', 'place'] as const;

export type FavoriteKind = (typeof FAVORITE_KINDS)[number];

/**
 * `source` of `search_results` (#379): the place picker, the station search (results
 * near a place), the map's search bar, and My weather's one search for places and
 * stations by name (FE #381).
 */
export const SEARCH_SOURCES = [
  'places',
  'stations',
  'map',
  'my_weather',
] as const;

export type SearchSource = (typeof SEARCH_SOURCES)[number];

/**
 * `place_results` of `search_results`: how many places the autocomplete offered,
 * which is at most five, so 0 to 4 and then `5_plus`. A count, never the
 * query.
 */
export const placeResultsBucket = (count: number): string => {
  if (!(count > 0)) {
    return '0';
  }
  return count >= 5 ? '5_plus' : String(Math.floor(count));
};

/**
 * The radius `station_results` counts within. The station list is sorted by
 * distance and paged by 20, so its raw length is nearly always 20; what varies is
 * how many of the listed stations are near the searched place.
 */
export const NEARBY_STATION_RADIUS_KM = 25;

/** How many stations lie within NEARBY_STATION_RADIUS_KM (km `distance`). */
export const countNearbyStations = (
  stations: ReadonlyArray<{distance?: number}>,
): number =>
  stations.filter(
    (station) =>
      typeof station.distance === 'number' &&
      station.distance <= NEARBY_STATION_RADIUS_KM,
  ).length;

/**
 * `station_results` of `search_results`: how many of the listed stations are
 * within NEARBY_STATION_RADIUS_KM of the searched place, read from the first
 * page (20 stations), so the top bucket is a full page.
 */
export const stationResultsBucket = (count: number): string => {
  if (!(count > 0)) {
    return '0';
  }
  if (count <= 5) {
    return '1_5';
  }
  return count < 20 ? '6_19' : '20_plus';
};

/** What a search found, as raw counts; each app adds the `source`. */
export type SearchCounts = {placeResults?: number; stationResults?: number};

/** The bucketed `search_results` parameters for the counts given. */
export const searchCountParams = (
  counts: SearchCounts,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (counts.placeResults != null) {
    params.place_results = placeResultsBucket(counts.placeResults);
  }
  if (counts.stationResults != null) {
    params.station_results = stationResultsBucket(counts.stationResults);
  }
  return params;
};
